import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { Link } from '@studio/legacy-components';
import { queryExperienceGoal, queryExperienceUsers } from 'next/client/queries';
import {
  selectExperienceUsersAnalytics,
  readAnalytics,
  selectExperienceGoalAnalytics,
} from 'next/entities/analytics';
import { GoalShape, selectGoal } from 'next/entities/goals';
import { OverviewMetricsContainer } from './styled';
import MetricCard from './MetricCard';

const calculatePercentage = (numerator, denominator) => {
  const result = numerator / denominator;

  if (denominator === 0 || !Number.isFinite(result)) {
    return '0%';
  }

  return `${Math.floor(result * 100)}%`;
};

export function OverviewMetrics({
  id,
  onLoad,
  usersAnalytics,
  goal,
  goalAnalytics,
}) {
  const loadingUsersAnalytics = !usersAnalytics;
  const loadingGoalAnalytics = !goalAnalytics;

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const { usersMetrics, usersCompletedMetrics } = useMemo(() => {
    const [userQueryResult] = usersAnalytics || [];

    if (!userQueryResult) {
      const emptyMetric = {
        metric: '-',
        caption: null,
      };

      return {
        usersMetrics: emptyMetric,
        usersCompletedMetrics: emptyMetric,
      };
    }

    const {
      experience_completed_users: completedUsers,
      experience_users: users,
      experiences_completed: completed,
      experiences_shown: shown,
    } = userQueryResult;

    return {
      usersMetrics: {
        metric: users.toLocaleString(),
        caption: `${shown.toLocaleString()} total ${pluralize(
          'time',
          shown
        )} shown`,
      },
      usersCompletedMetrics: {
        metric: calculatePercentage(completedUsers, users),
        caption: `${calculatePercentage(
          completed,
          shown
        )} of total users completed`,
      },
    };
  }, [usersAnalytics]);

  const goalMetrics = useMemo(() => {
    const goalQueryResult = (goalAnalytics || []).find(
      analytics => analytics.experience_id === id
    );

    if (!goal || !goalQueryResult) {
      return {
        metric: '-',
        caption: null,
      };
    }

    const { experience_users: users, goal_achievements: achievements } =
      goalQueryResult;

    return {
      metric: calculatePercentage(achievements, users),
      caption: (
        <>
          {achievements} unique users achieved
          <Link to={`/goals/${goal.id}`}> {goal.name}</Link>
        </>
      ),
    };
  }, [goalAnalytics, goal, id]);

  return (
    <OverviewMetricsContainer>
      <MetricCard
        title="Unique Users"
        metric={usersMetrics.metric}
        caption={usersMetrics.caption}
        tooltip="Understand your Flow's reach. This Flow was shown to these unique users over the selected time period."
        loading={loadingUsersAnalytics}
      />
      <MetricCard
        title="Unique Users Completed"
        metric={usersCompletedMetrics.metric}
        caption={usersCompletedMetrics.caption}
        loading={loadingUsersAnalytics}
      />
      <MetricCard
        title="Goal"
        metric={goalMetrics.metric}
        caption={goalMetrics.caption}
        tooltip="Understand how this Flow is helping you achieve an Appcues Goal during the selected time period."
        loading={loadingGoalAnalytics}
      />
    </OverviewMetricsContainer>
  );
}

OverviewMetrics.propTypes = {
  id: PropTypes.string,
  onLoad: PropTypes.func,
  usersAnalytics: PropTypes.arrayOf(
    PropTypes.shape({
      experience_completed_users: PropTypes.number,
      users: PropTypes.number,
      experiences_completed: PropTypes.number,
      experiences_shown: PropTypes.number,
    })
  ),
  goalAnalytics: PropTypes.arrayOf(
    PropTypes.shape({
      experience_completed_users: PropTypes.number,
      users: PropTypes.number,
      experiences_completed: PropTypes.number,
      experiences_shown: PropTypes.number,
    })
  ),
  goal: GoalShape,
};

const getQueryParams = ({ id, startTime, endTime, segmentId, goalId }) => ({
  experienceId: id,
  startTime,
  endTime,
  segmentId,
  goalId,
});

const mapStateToProps = (state, ownProps) => {
  const { goalId } = ownProps;
  const queryParams = getQueryParams(ownProps);
  const goal = selectGoal(state, goalId);

  return {
    usersAnalytics: selectExperienceUsersAnalytics(state, queryParams),
    goalAnalytics: goalId
      ? selectExperienceGoalAnalytics(state, queryParams)
      : [],
    goal,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const queryParams = getQueryParams(ownProps);
  const usersQuery = queryExperienceUsers(queryParams);
  const goalQuery = queryExperienceGoal(queryParams);

  return {
    onLoad: () => {
      dispatch(readAnalytics(usersQuery));

      /* Only fetch goal metrics if goal is set */
      if (queryParams.goalId) {
        dispatch(readAnalytics(goalQuery));
      }
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OverviewMetrics);
